import {
  ButtonGroup,
  Collapse,
  Link as MuiLink,
  useMediaQuery,
} from '@material-ui/core';
import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import {
  useAssignedProduct,
  useAssignedProductsLite,
  useDeleteAssignedProduct,
  useSetAssignedProductCustomImg,
} from 'app/core/api/product-assignment/api-product-assignment';
import {
  isFullAssignment,
  productImg,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import { useAssignmentOpenRepair } from 'app/core/api/repairer/api-repairer';
import { getErrorMsg } from 'app/core/errors/error-utils';
import AppImage from 'app/shared/components/app-image/AppImage';
import EditableImage from 'app/shared/components/app-image/EditableImage';
import { ProductPictureFallback } from 'app/shared/components/app-image/ProductPictureOrDefault';
import Button from 'app/shared/components/button/Button';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  OpenInSvg,
} from 'app/shared/components/icon/icon-lib';
import NameValueList from 'app/shared/components/list/NameValueList';
import Subheading from 'app/shared/components/typography/subheading/Subheading';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import ClassName from 'app/styles/ClassName';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import {
  useEffectOnChanges,
  useObservableBinding,
  useUrlParams,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import _ from 'lodash';
import React, { useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components/macro';
import NameValueGrid from '../../../../shared/components/list/NameValueGrid';
// import ProductDetails from '../../inprogress/ProductDetail';
import IconBox from '../components/IconBox';
import NotFoundRedirect from '../components/NotFoundRedirect';
import WarrantySummary from './components/WarrantySummary';
import {
  getAssignedProductDetailsList,
  getProductDetailsList,
} from './view-assigned-product-utils';

const StyledSubheading = styled(Subheading)`
  opacity: 0.75;
`;

const StyledIconBox = styled(IconBox)`
  width: 200px;
  max-width: 100%;
  height: 90px;
  ${styledMixins.collapseFix}
`;

const SQUARE_BTN_WIDTH_VAL = 50;
const CompactButton = styled(Button).attrs({
  slim: true,
  className: ClassName.NO_HEIGHT_MARGIN,
})`
  margin-top: ${styledFuncs.pad()};
  margin-bottom: ${styledFuncs.pad(0.5)};
`;
const DeleteButton = styled(CompactButton).attrs({ variant: 'text' })`
  &,
  &:hover {
    color: ${styledConsts.cError};
  }
`;
const GRP_LEFT_BTN_BREAKPOINT = '(min-width: 340px)';
const CompactGroupLeftButton = styled(CompactButton)`
  flex-grow: 1;
  @media ${GRP_LEFT_BTN_BREAKPOINT} {
    padding-left: ${styledFuncs.pad(0.5)};
    padding-right: ${styledFuncs.pad(0.5)};
    > .MuiButton-label {
      margin-left: ${SQUARE_BTN_WIDTH_VAL}px;
    }
  }
`;
const CompactSquareButton = styled(CompactButton)`
  flex-grow: 0;
  width: ${SQUARE_BTN_WIDTH_VAL}px;
`;

const PadCollapse = styled(Collapse).attrs({
  className: ClassName.NO_HEIGHT_MARGIN,
})`
  align-self: stretch;
  ${styledMixins.collapseFix}

  .MuiCollapse-wrapper {
    padding: ${styledFuncs.pad()};
    padding-left: ${styledFuncs.pad(1)};
    padding-right: ${styledFuncs.pad(1)};
  }
`;
const NoPadCollapse = styled(PadCollapse)`
  .MuiCollapse-wrapper {
    padding: 0;
    padding-top: ${styledFuncs.pad()};
  }
`;

const StyledList = styled(NameValueList)`
  border-top: none;
  margin-bottom: ${styledFuncs.pad(2)};
`;

const ImagePreview = styled(AppImage)`
  margin-top: ${styledFuncs.pad(-2)};
  margin-bottom: ${styledFuncs.pad(3)};
`;

const LinkContainer = styled.div`
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      margin-left: ${styledFuncs.pad()};
      width: 28px;
      height: 28px;
      path {
        fill: ${styledConsts.cPrimary};
      }
    }
  }
`;

export default function ViewAssignedProduct(): JSX.Element {
  const id = +useUrlParams(['id']).id;
  const history = useHistory();
  const { showDialog, showSnack } = useShowInfo();

  const [showInfo, toggleShowInfo] = useReducer(s => !s, false);
  const [showTerms, toggleShowTerms] = useReducer(s => !s, false);
  const [showReceipt, setShowReceipt] = useState(false);

  const {
    resource: asgnProductsLite,
    request: refreshAsgnProductsLite,
  } = useAssignedProductsLite();
  const {
    resource: asgnProductOrLite = _.find(asgnProductsLite, { id }),
    request: refreshAsgnProduct,
    loading: loadingAsgnProduct,
    error,
  } = useAssignedProduct(id, { fresh: true });
  const { request$: setProductImg$, sending: settingImg } =
    useSetAssignedProductCustomImg();
  const { request$: deleteAssignedProduct$, sending: deletingImg } =
    useDeleteAssignedProduct();
  const { resource: openRepairCaseId, loading: loadingOpenCase } =
    useAssignmentOpenRepair(id);

  const { productDetails: product, warrantyDetails: warranty } =
    asgnProductOrLite || {};

  const handleImageSelect = (img: File | null) => {
    setProductImg$({
      id,
      data: { userProductImage: img ?? undefined },
    }).subscribe(() => {
      refreshAsgnProduct();
      refreshAsgnProductsLite();
    });
  };
  const handleImageSelectRef = useWatchAsRef(handleImageSelect);

  const handleDeleteSuccess = () => {
    showSnack({
      message: 'Product deleted',
      alertType: 'success',
    });
    history.replace(SharedRoutePath.HOME);
  };

  const handleDeleteError = (e: unknown) => {
    showSnack({ message: getErrorMsg(e), alertType: 'error' });
  };

  const deleteConfirmCallback = () =>
    deleteAssignedProduct$(id).subscribe({
      next: handleDeleteSuccess,
      error: handleDeleteError,
    });

  const handleDeleteRequest = () =>
    showDialog({
      title: 'Delete assigned product',
      message:
        'Are you sure you want to delete this entry? You will lose the personal information associated with this product.',
      actions: [
        { text: 'Cancel' },
        {
          text: 'Confirm Delete',
          callback: () => {
            deleteConfirmCallback();
          },
        },
      ],
    });
  const spinnerStyling = useMediaQuery(GRP_LEFT_BTN_BREAKPOINT)
    ? { marginLeft: '8px', marginRight: '0px' }
    : {};

  const [imageSet, imageSet$] = useObservableBinding<File | null>(
    null
  );

  useEffectOnChanges(() => {
    handleImageSelectRef.current(imageSet);
  }, [handleImageSelectRef, imageSet]);

  const removeImage = () => {
    handleImageSelect(null);
    refreshAsgnProduct();
    refreshAsgnProductsLite();
  };

  if (error) return <NotFoundRedirect />;

  return (
    <>
      <StyledSubheading>{product?.name}</StyledSubheading>
      {asgnProductOrLite?.productDetails?.source ===
      'manual_input' ? (
        <EditableImage
          alt={product?.name || 'product'}
          imageSet$={imageSet$}
          currentSrc={productImg(asgnProductOrLite)}
          loadingImg={!asgnProductOrLite || settingImg || deletingImg}
          usedCustomImg={!!asgnProductOrLite?.userProductImage}
          fallbackImgNode={<ProductPictureFallback height="180px" />}
          classes={{ img: 'rounded', appImage: 'h-[180px]' }}
          removeImage={removeImage}
        />
      ) : (
        <div style={{ margin: '0 auto' }}>
          <AppImage
            alt="profile image"
            src={asgnProductOrLite?.productDetails?.imageUrl}
            fallbackImgNode={
              <ProductPictureFallback height="180px" />
            }
            height="180px"
            noPad
            className="mb-1.5 w-10 h-10 rounded"
          />
        </div>
      )}

      <StyledIconBox wide assignedProduct={asgnProductOrLite} />
      <>
        <ButtonGroup
          variant="outlined"
          color="primary"
          className={ClassName.NO_HEIGHT_MARGIN}
          css="flex: 0 0"
        >
          <CompactGroupLeftButton
            pending={loadingAsgnProduct}
            onClick={toggleShowInfo}
            {...{ spinnerStyling }}
          >
            {/* {showInfo ? 'Hide' : 'Show'} */}
            View / edit product info
          </CompactGroupLeftButton>
          <CompactSquareButton
            disabled={!asgnProductOrLite}
            aria-label="edit product"
            to={
              asgnProductOrLite
                ? {
                    pathname:
                      ConsumerRoutePath.ADD_ASSIGNED_PRODUCT_DETAILS,
                    query: {
                      [UrlQueryKey.PRODUCT_ASSIGNMENT_ID]:
                        asgnProductOrLite.id.toString(),
                      [UrlQueryKey.PRODUCT_VARIANT_ID]:
                        asgnProductOrLite.productDetails.id,
                      [UrlQueryKey.PRODUCT_VARIANT_NAME]:
                        asgnProductOrLite.productDetails.name,
                    },
                  }
                : undefined
            }
          >
            <Icon icon={IconLib.EDIT} />
          </CompactSquareButton>
        </ButtonGroup>
        <PadCollapse in={showInfo}>
          {isFullAssignment(asgnProductOrLite) && (
            <>
              <StyledList
                noSidePadding
                listItems={getAssignedProductDetailsList(
                  asgnProductOrLite,
                  showReceipt
                )}
              />
              <ImagePreview
                alt="uploaded receipt"
                src={asgnProductOrLite.receiptImage}
                onLoad={() => setShowReceipt(true)}
              />
              <NameValueGrid
                listItems={getProductDetailsList(
                  asgnProductOrLite.productDetails
                )}
              />
            </>
          )}
        </PadCollapse>
      </>
      {warranty ? (
        <>
          <CompactButton variant="outlined" onClick={toggleShowTerms}>
            {(showTerms && 'Hide') || 'Show'} warranty terms
          </CompactButton>
          <NoPadCollapse in={showTerms}>
            <WarrantySummary {...{ warranty }} />
            <LinkContainer>
              {warranty.url && (
                <MuiLink href={warranty.url}>
                  View full manufacturer terms
                  <OpenInSvg />
                </MuiLink>
              )}
            </LinkContainer>
          </NoPadCollapse>
        </>
      ) : (
        <CompactButton variant="contained" disabled>
          Warranty terms pending
        </CompactButton>
      )}
      {asgnProductOrLite?.productDetails?.userManual && (
        <CompactButton
          variant="outlined"
          to={`${SharedRoutePath.VIEW_MANUAL}/${id}`}
        >
          View Manual
        </CompactButton>
      )}
      <CompactButton
        to={
          openRepairCaseId
            ? `${SharedRoutePath.CHAT}/${openRepairCaseId}`
            : `${ConsumerRoutePath.PRODUCT_REPORT_FAULT}/${id}`
        }
        pending={loadingOpenCase}
      >
        {loadingOpenCase
          ? 'Repair'
          : openRepairCaseId
          ? 'View Repair'
          : 'Repair item'}
        {/* I have an issue with the item */}
      </CompactButton>
      {/* <CompactButton to={`${SharedRoutePath.VIEW_MANUAL}`}>
          View Manual
        </CompactButton> */}
      <DeleteButton onClick={handleDeleteRequest}>
        Delete product
      </DeleteButton>
    </>
  );
}
